import psHttp from '@patsnap/common-ps-http';
import {
    createCreditsChargeSdk,
    type CreditsChargeLocale,
} from '@patsnap/credits-charge';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { EUREKA_SERVICE_URI } from '@telecom/shared/domain-config/config';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import { isUserLogin } from '@telecom/shared/startup/user-auth';
import { getIsOversea } from '@telecom/shared/utils/environment';
import type {
    CreditsChargeInitializationResult,
    CreditsChargeService,
} from '../credits-charge-service';

/** Credits Charge SDK 支持的语言。 */
const CREDITS_CHARGE_LOCALES: readonly CreditsChargeLocale[] = ['cn', 'en', 'jp', 'tw'];
/** SDK 成功 Toast 的展示时长。 */
export const CREDITS_CHARGE_TOAST_DURATION_MS = 5000;

/** 使用 Credits Charge SDK 执行宿主侧积分领取。 */
@injectable()
export class CreditsChargeServiceImpl implements CreditsChargeService {
    constructor(
        /** 提供当前应用语言。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
    ) {}

    /** 校验登录态和区域后调用 SDK，并将异常归一化为业务结果。 */
    async checkAndClaim(): Promise<CreditsChargeInitializationResult> {
        try {
            const isLogin = await isUserLogin();
            if (!isLogin) {
                return { status: 'skipped', reason: 'not-logged-in' };
            }
            if (!getIsOversea()) {
                return { status: 'skipped', reason: 'not-oversea' };
            }

            const locale = this._resolveLocale();
            const creditsChargeSdk = createCreditsChargeSdk({
                baseURL: EUREKA_SERVICE_URI,
                http: psHttp,
                locale,
                toastDuration: CREDITS_CHARGE_TOAST_DURATION_MS,
            });
            return await creditsChargeSdk.checkAndClaim();
        }
        catch (error) {
            console.warn('Credits charge initialization failed:', error);
            return {
                status: 'error',
                stage: 'recharge',
                error,
            };
        }
    }

    /** 将当前应用语言映射为 SDK 支持语言。 */
    private _resolveLocale(): CreditsChargeLocale {
        const currentLocale = this._i18nService.locale.value as CreditsChargeLocale;
        return CREDITS_CHARGE_LOCALES.includes(currentLocale) ? currentLocale : 'en';
    }
}
